import React, { useState, useEffect, useMemo, useCallback } from 'react';
import notify from '../utils/notify';
import { useNavigate, useLocation } from 'react-router-dom';
import Step1_Details from './Step1_Details';
import Step1_QuickSetup from './Step1_QuickSetup';
import Step2_Courses from './Step2_Courses';
import Step3_Players from './Step3_Players';
import Step3_RollUpConfig from './Step3_RollUpConfig';
import Step3_Scoring from './Step3_Scoring';
import Step4_OrganizeGroups from './Step4_OrganizeGroups';
import ConfirmationModal from './ConfirmationModal';
import EventTypeSelector, { EVENT_TYPES } from './EventTypeSelector';
import './AddTournamentWizard.css';
import { useQueryClient } from 'react-query';
import { useApi } from '../useApi';
import { useAuth } from '../AuthContext';
import { Permission } from '../permissions';
import SocietyHeaderBadge from './SocietyHeaderBadge';
import BottomNav from '../BottomNav';
import { FiBarChart2, FiGrid, FiSettings, FiChevronLeft, FiAward, FiX } from 'react-icons/fi';
import { playerLimitMessage, roundLimitMessage } from '../utils/competitionLimits';
import { applyRegularRound, regularsPool, seedSelf } from '../utils/regularRound';
import { readQuickDraft, writeQuickDraft, clearQuickDraft } from '../utils/quickRoundDraft';

// A pristine draft. Built by a factory rather than a module constant because
// switching event type re-seeds the draft from it, and a shared object would
// carry the previous attempt's courses/players across in its arrays.
const emptyTournamentData = (initialFormat) => ({
    id: null,
    name: '',
    date: '',
    format: initialFormat === 'matchplay' ? 'Singles Matchplay' : 'Stableford',
    matchplay_percentage: initialFormat === 'matchplay' ? 100 : 95,
    use_simple_handicap: false,
    signup_mode: 'standard',
    is_shotgun: false,
    tee_sheet_config: null,
    courses: [],
    players: [],
    groups: [],
    // {round_number: {player_id: tee_id}} — per-player tee assignments made on
    // the Assign Groups step, for rounds where more than one tee is in play.
    // Empty for every single-tee event.
    player_tees: {},
});


const AddTournamentWizard = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const urlQuickMode = searchParams.get('mode') === 'quick';
    const initialFormat = searchParams.get('format');
    // `?type=roll_up` opens straight onto that event type, skipping the
    // selector — Play Golf offers "New Roll Up" as its own action, and it must
    // start the draft with the same defaults the selector would have applied.
    const urlEventType = EVENT_TYPES.find(t => t.key === searchParams.get('type') && !t.route);

    const { authenticatedFetch } = useApi();
    const { hasPermission, canCreateRound, currentSociety } = useAuth();
    const queryClient = useQueryClient();

    // Members (can create quick rounds but not full tournaments) always use the quick-mode wizard
    const memberMode = canCreateRound && !hasPermission(Permission.CREATE_TOURNAMENT);
    const isQuickMode = urlQuickMode || memberMode || !!urlEventType?.quickMode;
    
    // An unfinished setup on this phone (utils/quickRoundDraft) — a quick
    // round or a competition, each in its own slot. Not for an edit, and not
    // when the round was started from "play with your regulars", which is an
    // explicit fresh start.
    const draftKind = isQuickMode ? 'quick' : 'full';
    const draftSocietyId = !location.state?.tournament && !location.state?.useRegulars
        ? currentSociety?.id : null;
    const [restoredDraft, setRestoredDraft] = useState(() => {
        const draft = readQuickDraft(draftSocietyId, draftKind);
        // "New Roll Up" from Play Golf asks for THAT event; a draft of another
        // kind waits for the visit that asks for it.
        if (draft && !isQuickMode && urlEventType && draft.eventTypeKey !== urlEventType.key) return null;
        if (draft && !isQuickMode && !draft.eventTypeKey) return null;
        return draft;
    });
    const [currentStep, setCurrentStep] = useState(
        location.state?.initialStep || restoredDraft?.step || 1);
    const [isEditMode, setIsEditMode] = useState(false);
    const [eventTypeSelected, setEventTypeSelected] = useState(isQuickMode || !!urlEventType || !!restoredDraft);
    // Set by the first tap or keystroke inside the form, so Cancel on an EDIT
    // only asks when somebody actually changed something. Not derived from the
    // data: several steps normalise it on mount, which would read as an edit.
    const [touched, setTouched] = useState(false);
    const [confirmLeave, setConfirmLeave] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [createdTournamentId, setCreatedTournamentId] = useState(null);
    const [allCourses, setAllCourses] = useState([]); // Fetched at top level for Quick Mode
    const [availableOoms, setAvailableOoms] = useState([]); // manual-mode OOMs to link to
    const [oomLinks, setOomLinks] = useState(() => restoredDraft?.oomLinks || []); // selected OOM ids
    const [groupsSelectedRound, setGroupsSelectedRound] = useState(location.state?.initialRound || 1);

    // Play Golf's "Start with your regulars" hands the wizard a head start: the
    // course you play most (with the tee you last played it off) to open on,
    // and the players you play with most to OFFER on the players step. The
    // course is seeded because it's one answer; the OTHERS are not, because a
    // guessed fourball would enter people in a round they aren't playing.
    //
    // You are the exception: whoever starts a round is playing in it, so you go
    // straight into the roster. Remove yourself and you come back as the first
    // chip on the offer row, so it stays a correction rather than a dead end.
    const { me: myPlayer, regulars, usualCourse } = location.state || {};

    // AND THE SAME OFFER WHEN NOBODY HANDED IT OVER. Setting a round or a
    // competition up from Manage is the other half of the traffic through this
    // wizard, and it arrived with no state at all — so the one feature that
    // saves an organiser the most typing was reachable only from the one
    // screen that happened to pass it in. The wizard asks for itself now.
    //
    // Only the PLAYERS, not the course: arriving here from Play Golf's button
    // is a statement that you want your usual round, while opening the wizard
    // from Manage is not, and seeding a course nobody asked for is a decision
    // taken away rather than a step saved. The course is dealt with by putting
    // the society's most-played first (utils/courseOrder.js).
    const [fetchedRegulars, setFetchedRegulars] = useState(null);
    // You, as the server knows you — kept so that switching event type, which
    // starts a clean draft, can put you straight back in it.
    const [fetchedMe, setFetchedMe] = useState(null);
    useEffect(() => {
        // `isEditMode` is only set once the tournament has loaded, so the
        // state it loads FROM is what can be checked at mount.
        if (regulars || location.state?.tournament) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const res = await authenticatedFetch('/me/regulars');
                if (!res.ok) return;
                const body = await res.json();
                if (cancelled) return;
                setFetchedRegulars(regularsPool(body));
                setFetchedMe(body.me || null);

                // "Your regular round" asked for the WHOLE thing — the course
                // you play most, off the tee you last played it from, with you
                // already in. It used to arrive in navigation state, which
                // meant Play Golf had to await this request before it could
                // move; the tap is instant now and the answer is applied here.
                // The rules for applying it late are in utils/regularRound.
                // Every other way in still puts YOU in: whoever sets a round
                // or a competition up is playing in it (utils/regularRound
                // seedSelf). This was only ever done for the regular round, so
                // a quick round or a competition opened on an empty roster and
                // the organiser had to search the society for their own name.
                setTournamentData((prev) => (location.state?.useRegulars
                    ? applyRegularRound(prev, body)
                    : seedSelf(prev, body.me)));
            } catch {
                // An offer nobody can make is simply not offered.
            }
        })();
        return () => { cancelled = true; };
        // Once, on arrival: this is a convenience, not a dependency of the form.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const suggestedPlayers = useMemo(
        () => (myPlayer ? [myPlayer, ...(regulars || [])] : (regulars || fetchedRegulars)),
        [myPlayer, regulars, fetchedRegulars],
    );

    const [tournamentData, setTournamentData] = useState(() => {
        if (restoredDraft?.data) return restoredDraft.data;
        const draft = { ...emptyTournamentData(initialFormat), ...(urlEventType?.defaults || {}) };
        if (myPlayer) draft.players = [myPlayer];
        if (usualCourse?.id) {
            draft.courses = [{
                id: usualCourse.id,
                name: usualCourse.name,
                sequence_number: 1,
                tee_id: usualCourse.tee_id || null,
                tee_name: usualCourse.tee_name || null,
                tees_in_play: usualCourse.tee_id ? [usualCourse.tee_id] : [],
            }];
        }
        return draft;
    });
    // Which event type the current draft belongs to, so backing out to the
    // selector and picking a different one starts clean (see handleEventTypeSelect).
    const [eventTypeKey, setEventTypeKey] = useState(
        urlEventType?.key || restoredDraft?.eventTypeKey || (isQuickMode ? 'single_round' : null));
    const [originalTournamentData, setOriginalTournamentData] = useState(null);
    const [playerPickerOpen, setPlayerPickerOpen] = useState(false);

    // Redirect users without create permission away from event type selector
    useEffect(() => {
        if (!isQuickMode && !isEditMode && !hasPermission(Permission.CREATE_TOURNAMENT) && !canCreateRound) {
            navigate('/play', { replace: true });
        }
        if (isQuickMode && !canCreateRound) {
            navigate('/play', { replace: true });
        }
    }, [isQuickMode, isEditMode, hasPermission, canCreateRound, navigate]);

    // Fetch courses on mount (needed immediately for Quick Mode Step 1)
    useEffect(() => {
        const fetchCourses = async () => {
            try {
                const response = await authenticatedFetch('/courses');
                if (response.ok) {
                    const courses = await response.json();
                    setAllCourses(courses);
                }
            } catch (error) {
                console.error("Failed to fetch courses:", error);
            }
        };
        fetchCourses();
    }, [authenticatedFetch]);

    // Memoize unassigned players (only strictly needed for Full Wizard Step 4 validation)
    const unassignedPlayers = useMemo(() => {
        const allAssignedPlayerIds = new Set(
            tournamentData.groups.flatMap(roundGroup =>
                roundGroup.groups.flatMap(group => group.players.map(p => p.id))
            )
        );
        return tournamentData.players.filter(player => !allAssignedPlayerIds.has(player.id));
    }, [tournamentData.players, tournamentData.groups]);

    const isTournamentStarted = useMemo(() => {
        return tournamentData.courses?.some(c => c.is_started) ||
               tournamentData.groups?.some(rg => rg.is_started);
    }, [tournamentData.courses, tournamentData.groups]);

    // Multi-round sub-stepper on the Groups screen
    const availableRoundNumbers = useMemo(() => {
        const nums = (tournamentData.courses || []).map(c => c.sequence_number);
        return [...new Set(nums)].sort((a, b) => a - b);
    }, [tournamentData.courses]);

    // Keep groupsSelectedRound in sync as courses load (edit mode) or initialRound changes
    useEffect(() => {
        if (availableRoundNumbers.length === 0) return;
        if (!availableRoundNumbers.includes(groupsSelectedRound)) {
            setGroupsSelectedRound(availableRoundNumbers[0]);
        }
    }, [availableRoundNumbers, groupsSelectedRound]);

    const isOnGroupsStep = useMemo(() => {
        if (isQuickMode) return currentStep === 3;
        return currentStep === 5 && tournamentData.signup_mode !== 'roll_up';
    }, [isQuickMode, currentStep, tournamentData.signup_mode]);

    const groupsCurrentRoundIndex = availableRoundNumbers.indexOf(groupsSelectedRound);
    const isOnLastGroupsRound = groupsCurrentRoundIndex === availableRoundNumbers.length - 1;
    const hasMultipleRounds = availableRoundNumbers.length > 1;
    const nextGroupsRoundNumber = isOnLastGroupsRound ? null : availableRoundNumbers[groupsCurrentRoundIndex + 1];

    // Advances to the next round without enforcing assignment of the current round —
    // the user may intentionally leave R2/R3 empty and come back later.
    const handleAdvanceRound = () => {
        if (nextGroupsRoundNumber != null) {
            setGroupsSelectedRound(nextGroupsRoundNumber);
            window.scrollTo(0, 0);
        }
    };

    useEffect(() => {
        const tournamentToEdit = location.state?.tournament;
        if (tournamentToEdit) {
            setIsEditMode(true);
            setEventTypeSelected(true);
            // Initialize with what we have (summary)
            setTournamentData(prev => ({
                ...prev,
                id: tournamentToEdit.id,
                name: tournamentToEdit.name || '',
                date: tournamentToEdit.date || '',
                format: tournamentToEdit.format || 'Stableford',
                matchplay_percentage: tournamentToEdit.matchplay_percentage || '',
                use_simple_handicap: tournamentToEdit.use_simple_handicap || false,
                signup_mode: tournamentToEdit.signup_mode || 'standard',
                is_shotgun: tournamentToEdit.is_shotgun || false,
                // courses/players/groups might be missing in summary, so we wait for fetch
            }));
            
            // Fetch FULL details to get courses, players, groups, and is_started flags
            const fetchFullDetails = async () => {
                try {
                    const res = await authenticatedFetch(`/tournaments/${tournamentToEdit.id}`);
                    if (res.ok) {
                        const fullData = await res.json();
                        const structuredGroups = fullData.groups && fullData.groups.length > 0 ? fullData.groups : [];
                        
                        setTournamentData(prev => ({
                            ...prev,
                            // Ensure we preserve the ID and core fields just in case
                            id: fullData.id,
                            name: fullData.name,
                            date: fullData.date,
                            format: fullData.format,
                            matchplay_percentage: fullData.matchplay_percentage,
                            use_simple_handicap: fullData.use_simple_handicap || false,
                            signup_mode: fullData.signup_mode,
                            is_shotgun: fullData.is_shotgun || false,
                            is_advanced: fullData.is_advanced || false,
                            tee_sheet_config: typeof fullData.tee_sheet_config === 'string'
                                ? JSON.parse(fullData.tee_sheet_config) 
                                : fullData.tee_sheet_config,
                            courses: fullData.courses || [],
                            players: fullData.players || [],
                            groups: structuredGroups,
                            player_tees: fullData.player_tees || {},
                        }));
                        
                        // Parse tee_sheet_config in original data too for stable comparison
                        const parsedOriginal = {
                            ...fullData,
                            tee_sheet_config: typeof fullData.tee_sheet_config === 'string' 
                                ? JSON.parse(fullData.tee_sheet_config) 
                                : fullData.tee_sheet_config
                        };
                        setOriginalTournamentData(parsedOriginal);
                    }
                } catch (error) {
                    console.error("Failed to fetch full tournament details:", error);
                }
            };
            fetchFullDetails();
        }
    }, [location.state, authenticatedFetch]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentStep]);

    // Manual-mode active Orders of Merit this event can be added to (admins
    // only; date-range OOMs auto-include by date so they're not offered here).
    // The list endpoint returns newest-first, so [0] is the latest created.
    useEffect(() => {
        if (memberMode) return;
        (async () => {
            try {
                const res = await authenticatedFetch('/oom');
                if (!res.ok) return;
                const all = await res.json();
                setAvailableOoms(all.filter(o =>
                    (o.config?.event_selection || 'manual') === 'manual' && o.status === 'active'));
            } catch (e) { /* ignore */ }
        })();
    }, [authenticatedFetch, memberMode]);

    // For a NEW tournament, default to qualifying for the latest active OOM.
    // A restored draft already holds the organiser's answer, which may be "none".
    const [oomDefaulted, setOomDefaulted] = useState(!!restoredDraft?.oomLinks);
    useEffect(() => {
        if (oomDefaulted || memberMode || isEditMode || availableOoms.length === 0) return;
        setOomLinks([availableOoms[0].id]);
        setOomDefaulted(true);
    }, [availableOoms, isEditMode, memberMode, oomDefaulted]);

    // QUICK ROUND: Basics (scoring folded in) · Players · Groups — and no Groups
    // step at all when everyone fits in one group, because a four-ball can only
    // be arranged one way. It used to be four steps, two of them ceremony: a
    // full screen of scoring defaults almost nobody changes, and an empty slot
    // per player to fill one tap at a time. Singles matchplay plays in pairs,
    // and an edit keeps the step, so neither is ever skipped.
    const quickOneGroup = isQuickMode && !isEditMode
        && tournamentData.players.length > 0 && tournamentData.players.length <= 4
        && !/singles matchplay/i.test(tournamentData.format || '');

    const totalSteps = useMemo(() => {
        if (isQuickMode) return quickOneGroup ? 2 : 3; // Basics, Players[, Groups]
        if (tournamentData.signup_mode === 'roll_up') return 4; // Basics, Courses, Scoring, Tee Sheet
        return 5; // Standard: Basics, Courses, Scoring, Players, Groups
    }, [isQuickMode, quickOneGroup, tournamentData.signup_mode]);

    // Players taken off on step 2 can collapse the Groups step under you.
    useEffect(() => {
        if (currentStep > totalSteps) setCurrentStep(totalSteps);
    }, [currentStep, totalSteps]);

    // Keep the unfinished setup on the phone. Only once there is something
    // worth keeping — past the first step, a name, a course, or players beyond
    // the one the wizard seeds (you). Never while the event-type picker is up:
    // there is no event yet to keep.
    const hasWorthKeeping = currentStep > 1 || tournamentData.players.length > 1 || !!tournamentData.name
        || (!isQuickMode && (tournamentData.courses || []).length > 0);
    useEffect(() => {
        if (!draftSocietyId || isEditMode) return;
        if (!isQuickMode && (!eventTypeSelected || !eventTypeKey)) return;
        if (hasWorthKeeping) {
            writeQuickDraft(draftSocietyId, tournamentData, currentStep, draftKind,
                isQuickMode ? {} : { eventTypeKey, oomLinks });
        }
    }, [draftSocietyId, draftKind, isEditMode, isQuickMode, eventTypeSelected, eventTypeKey,
        oomLinks, hasWorthKeeping, tournamentData, currentStep]);

    const startFresh = () => {
        clearQuickDraft(draftSocietyId, draftKind);
        setRestoredDraft(null);
        // A clean reload re-runs the arrival seeding (you, your usual course).
        window.location.reload();
    };

    // Returns an error message if `stepNum` isn't satisfied yet, or null if it's fine to move past it.
    // Shared by nextStep() (advancing forward) and canJumpToStep() (jumping via the progress bar).
    const validateStep = useCallback((stepNum) => {
        if (isQuickMode) {
            // Quick: 1 Basics (with scoring), 2 Players, 3 Groups
            if (stepNum === 1) {
                if (tournamentData.courses.length === 0) return 'Please select a course.';
                const tooManyRounds = roundLimitMessage(tournamentData.courses.length);
                if (tooManyRounds) return tooManyRounds;
                if (!tournamentData.name || !tournamentData.date) return 'Please provide a name and date.';
            }
            if (stepNum === 2) {
                if (tournamentData.players.length === 0) return 'Please add at least one player.';
                const tooMany = playerLimitMessage(tournamentData.players.length);
                if (tooMany) return tooMany;
            }
        } else {
            // Full: 1 Basics, 2 Courses, 3 Scoring, 4 Players, 5 Groups
            // Roll-up: 1 Basics, 2 Courses, 3 Scoring, 4 Tee Sheet
            if (stepNum === 1) {
                if (!tournamentData.name || !tournamentData.date) return 'Please fill in all tournament details before proceeding.';
            }
            if (stepNum === 2) {
                if (tournamentData.courses.length === 0) return 'Please add at least one course.';
                // A round IS a course here, so the round cap is a cap on this step.
                const tooMany = roundLimitMessage(tournamentData.courses.length);
                if (tooMany) return tooMany;
            }
            if (stepNum === 4 && tournamentData.signup_mode === 'standard') {
                if (tournamentData.players.length === 0) return 'Please add at least one player to the tournament.';
                const tooMany = playerLimitMessage(tournamentData.players.length);
                if (tooMany) return tooMany;
            }
            if (stepNum === 5 && tournamentData.signup_mode === 'standard') {
                if (unassignedPlayers.length > 0) return 'All players must be assigned to a group.';
                if (tournamentData.groups.length === 0) return 'Please create at least one group.';
                const hasInvalidGroupSize = tournamentData.groups.some(roundGroup =>
                    roundGroup.groups.some(group => group.players.length < 1)
                );
                if (hasInvalidGroupSize) return 'Each group must have at least 1 player.';
            }
        }
        return null;
    }, [isQuickMode, tournamentData, unassignedPlayers]);

    const nextStep = () => {
        const error = validateStep(currentStep);
        if (error) {
            notify.warn(error);
            return;
        }
        if (currentStep < totalSteps) {
             setCurrentStep(prev => prev + 1);
        }
    };

    const prevStep = () => setCurrentStep(prev => prev - 1);

    // In edit mode a step is directly reachable as soon as every step before it holds valid
    // data (the tournament already exists, so most steps are pre-populated) — no need to click
    // through each stage in order. Create mode keeps the original "only previously-visited
    // steps are clickable" behavior, since forward jumps there would skip real validation.
    const canJumpToStep = useCallback((stepNum) => {
        if (stepNum === currentStep) return false;
        if (!isEditMode) return stepNum < currentStep;
        for (let s = 1; s < stepNum; s++) {
            if (validateStep(s)) return false;
        }
        return true;
    }, [isEditMode, currentStep, validateStep]);

    const stepLabels = isQuickMode
        ? (quickOneGroup ? ['Basics', 'Players'] : ['Basics', 'Players', 'Groups'])
        : tournamentData.signup_mode === 'roll_up'
            ? ['Basics', 'Course', 'Scoring', 'Tee Sheet']
            : ['Basics', 'Courses', 'Scoring', 'Players', 'Groups'];

    // Progress Bar Logic — segments are clickable for already-visited steps
    const progressSteps = (
        <div className="progress-container">
            <div className="progress-bars-container">
                {Array.from({ length: totalSteps }).map((_, i) => {
                    const stepNum = i + 1;
                    const isActive = stepNum === currentStep;
                    const isCompleted = stepNum < currentStep;
                    const isReachable = canJumpToStep(stepNum);
                    return (
                        <div
                            key={i}
                            className={`progress-bar-step${isActive ? ' progress-bar-step--active' : isCompleted ? ' progress-bar-step--completed' : ' progress-bar-step--future'}${isReachable && !isActive && !isCompleted ? ' progress-bar-step--reachable' : ''}`}
                            onClick={() => isReachable && setCurrentStep(stepNum)}
                        >
                            <div className="progress-bar-segment" />
                            <span className="progress-step-label">{stepLabels[i]}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    const updateTournamentData = useCallback((data) => {
        setTournamentData(prev => {
            // Special handling for updating specific round groups
            if (data.groups !== undefined && data.round_number !== undefined) {
                const existingRoundGroupsIndex = prev.groups.findIndex(rg => rg.round_number === data.round_number);

                const roundUpdate = { groups: data.groups };
                if (data.pairingMode !== undefined) roundUpdate.pairingMode = data.pairingMode;

                let newGroupsArray;
                if (existingRoundGroupsIndex !== -1) {
                    newGroupsArray = [...prev.groups];
                    newGroupsArray[existingRoundGroupsIndex] = {
                        ...newGroupsArray[existingRoundGroupsIndex],
                        ...roundUpdate
                    };
                } else {
                    newGroupsArray = [...prev.groups, { round_number: data.round_number, ...roundUpdate }];
                }
                return { ...prev, groups: newGroupsArray };
            }
            // General merge for other fields
            return { ...prev, ...data };
        });
    }, [setTournamentData]);

    const isRollUp = tournamentData.signup_mode === 'roll_up';
    // Mirrors the Advanced Competition wizard's persistent Tee Sheet shortcut —
    // an admin editing an existing, not-yet-started roll-up day can jump
    // straight to the tee sheet from any step instead of paging to the last one.
    const showTeeShortcut = isEditMode && isRollUp && !isTournamentStarted && !!tournamentData.id;
    const rollUpConfigChanged = useMemo(() => {
        if (!isEditMode || !originalTournamentData) return false;
        const configSame = JSON.stringify(tournamentData.tee_sheet_config) === JSON.stringify(originalTournamentData.tee_sheet_config);
        const shotgunSame = tournamentData.is_shotgun === originalTournamentData.is_shotgun;
        return !configSame || !shotgunSame;
    }, [isEditMode, tournamentData.tee_sheet_config, tournamentData.is_shotgun, originalTournamentData]);

    const rollUpComplete = useMemo(() => {
        if (!isRollUp) return false;
        const config = tournamentData.tee_sheet_config;
        if (!config || !config.start_time || !config.num_groups) return false;
        
        if (tournamentData.is_shotgun) {
            return true;
        } else {
            return config.interval > 0;
        }
    }, [isRollUp, tournamentData.tee_sheet_config, tournamentData.is_shotgun]);

    const handleFinish = async () => {
        if (isSubmitting) return;

        let finalTournamentData = { ...tournamentData };

        // --- ROLL UP LOGIC ---
        if (finalTournamentData.signup_mode === 'roll_up') {
            if (!finalTournamentData.tee_sheet_config) {
                notify.warn('Please configure the tee sheet.');
                return;
            }
        }
        // --- QUICK MODE LOGIC ---
        else if (isQuickMode) {
             if (finalTournamentData.players.length === 0) {
                notify.warn('Please add at least one player to start the round.');
                return;
            }

            // One group, made here: the Groups step was skipped because there
            // is only one way to arrange it.
            if (quickOneGroup) {
                finalTournamentData = {
                    ...finalTournamentData,
                    groups: [{
                        round_number: 1,
                        pairingMode: 'manual',
                        groups: [{ id: 1, name: 'Group 1', players: finalTournamentData.players }],
                    }],
                };
            }

            // Validation for Manual Groups (Quick Mode)
            const manualGroupsForR1 = finalTournamentData.groups.find(rg => rg.round_number === 1);
            
            if (!manualGroupsForR1 || manualGroupsForR1.groups.length === 0) {
                 notify.warn('Please assign players to groups.');
                 return;
            }

            // Ensure all players are assigned
            const assignedIds = new Set(manualGroupsForR1.groups.flatMap(g => g.players.map(p => p.id)));
            const unassigned = finalTournamentData.players.filter(p => !assignedIds.has(p.id));
            if (unassigned.length > 0) {
                notify.warn('All players must be assigned to a group.');
                return;
            }
        } else {
            // --- FULL WIZARD VALIDATION ---
            // Rule: the FIRST round is mandatory (must be populated). Later rounds can be
            // left empty and assigned later — e.g. when R2 pairings depend on R1 results.
            const isRoundPopulated = (rg) => {
                if (!rg) return false;
                if (rg.pairingMode === 'leaderboard') return true;
                return (rg.groups || []).some(g => (g.players?.length || 0) > 0);
            };

            const firstRoundNumber = availableRoundNumbers[0];
            if (firstRoundNumber == null) {
                notify.warn('Please add at least one course before finishing setup.');
                return;
            }
            const firstRoundData = finalTournamentData.groups.find(rg => rg.round_number === firstRoundNumber);
            if (!isRoundPopulated(firstRoundData)) {
                notify.warn(`Please assign groups for Round ${firstRoundNumber} — the first round is required.`);
                return;
            }

            // Half-set rounds (some groups filled, others empty) are still rejected
            const populatedRounds = finalTournamentData.groups.filter(isRoundPopulated);
            const hasHalfSetRound = populatedRounds.some(rg => {
                if (rg.pairingMode === 'leaderboard') return false;
                return (rg.groups || []).some(g => !g.placeholder && (g.players?.length || 0) < 1);
            });
            if (hasHalfSetRound) {
                notify.warn('In rounds where you started assigning groups, each group must have at least 1 player.');
                return;
            }

            // Every tournament player must be in at least one round's groups
            // (leaderboard rounds exempt this since pairings come later)
            const hasLeaderboardRound = finalTournamentData.groups.some(rg => rg.pairingMode === 'leaderboard');
            if (!hasLeaderboardRound && unassignedPlayers.length > 0) {
                notify.warn('Every player must be assigned to at least one round’s groups.');
                return;
            }
        }

        // All validations passed, now set submitting state
        setIsSubmitting(true);

        const { id, name, date, format, matchplay_percentage, use_simple_handicap, courses, signup_mode, tee_sheet_config, is_shotgun } = finalTournamentData;

        const tournamentPayload = {
            name, date,
            format,
            matchplay_percentage: matchplay_percentage || 100,
            use_simple_handicap: use_simple_handicap || false,
            signup_mode,
            is_shotgun: is_shotgun || false,
            tee_sheet_config: JSON.stringify(tee_sheet_config)
        };

        const endpoint = isEditMode ? `/tournaments/${id}` : '/tournaments';
        const method = isEditMode ? 'PUT' : 'POST';

        try {
            // 1. Create/Update Tournament
            const response = await authenticatedFetch(endpoint, {
                method: method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(tournamentPayload),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Failed to ${isEditMode ? 'update' : 'create'} tournament details`);
            }

            let tournamentIdToUse = id;
            if (!isEditMode) {
                const newTournament = await response.json();
                tournamentIdToUse = newTournament.id;
            }
            
            // 2. Manage Players/Courses/Groups based on mode
            if (signup_mode === 'roll_up') {
                // For Roll Up, we just need to associate the course
                await authenticatedFetch(`/tournaments/${tournamentIdToUse}/courses`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ courses: courses.map(c => ({ id: c.id, sequence_number: c.sequence_number, tee_id: c.tee_id || null, tees_in_play: c.tees_in_play || [] })) }),
                });

                // Generate/Regenerate tee sheet if new OR config changed
                if (!isEditMode || rollUpConfigChanged) {
                    const generateRes = await authenticatedFetch(`/tournaments/${tournamentIdToUse}/generate-tee-sheet`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            ...tee_sheet_config,
                            course_id: courses[0].id,
                            round_number: 1
                        }),
                    });
                    if (!generateRes.ok) throw new Error("Failed to generate tee sheet");
                }

            } else {
                // Tournament roster comes from the Players step (Step 3), not from groups.
                // A player added in Step 3 but not yet placed in a group must still appear
                // in `tournament.players` — otherwise they vanish from Rounds/Leaderboard.
                const currentTournamentPlayerIds = finalTournamentData.players.map(p => p.id);

                if (isEditMode && originalTournamentData) {
                    const originalPlayerIds = originalTournamentData.players.map(p => p.id);
                    const playersToAdd = currentTournamentPlayerIds.filter(pid => !originalPlayerIds.includes(pid));
                    const playersToRemove = originalPlayerIds.filter(pid => !currentTournamentPlayerIds.includes(pid));

                    if (playersToAdd.length > 0) {
                        await authenticatedFetch(`/tournaments/${tournamentIdToUse}/players`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ player_ids: playersToAdd }),
                        });
                    }
                    if (playersToRemove.length > 0) {
                        await authenticatedFetch(`/tournaments/${tournamentIdToUse}/players`, {
                            method: 'DELETE',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ player_ids: playersToRemove }),
                        });
                    }
                } else {
                    // New Tournament (Quick or Full)
                    await authenticatedFetch(`/tournaments/${tournamentIdToUse}/players`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ player_ids: currentTournamentPlayerIds }),
                    });
                }

                // 3. Manage Courses
                if (isEditMode && originalTournamentData) {
                    const originalCourses = originalTournamentData.courses.map(c => ({ id: c.id, sequence_number: c.sequence_number, tee_id: c.tee_id || null, tees_in_play: c.tees_in_play || [] }));
                    const currentCourses = courses.map(c => ({ id: c.id, sequence_number: c.sequence_number, tee_id: c.tee_id || null, tees_in_play: c.tees_in_play || [] }));

                    const coursesToAdd = currentCourses.filter(cc =>
                        !originalCourses.some(oc => oc.id === cc.id && oc.sequence_number === cc.sequence_number)
                    );
                    const coursesToRemove = originalCourses.filter(oc =>
                        !currentCourses.some(cc => cc.id === oc.id && cc.sequence_number === oc.sequence_number)
                    );
                    // Courses that exist in both but tee_id changed
                    // Changed main tee OR changed set of tees in play — the
                    // latter matters on its own, since adding a second tee to a
                    // round leaves the main tee untouched.
                    const sameTeeSet = (a, b) =>
                        JSON.stringify([...(a || [])].sort()) === JSON.stringify([...(b || [])].sort());
                    const coursesToUpdateTee = currentCourses.filter(cc =>
                        originalCourses.some(oc => oc.id === cc.id
                            && oc.sequence_number === cc.sequence_number
                            && (oc.tee_id !== cc.tee_id || !sameTeeSet(oc.tees_in_play, cc.tees_in_play)))
                    );

                    if (coursesToAdd.length > 0 || coursesToUpdateTee.length > 0) {
                        await authenticatedFetch(`/tournaments/${tournamentIdToUse}/courses`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ courses: [...coursesToAdd, ...coursesToUpdateTee] }),
                        });
                    }
                    if (coursesToRemove.length > 0) {
                        await authenticatedFetch(`/tournaments/${tournamentIdToUse}/courses`, {
                            method: 'DELETE',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ courses: coursesToRemove }),
                        });
                    }
                } else {
                    await authenticatedFetch(`/tournaments/${tournamentIdToUse}/courses`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ courses: courses.map(c => ({ id: c.id, sequence_number: c.sequence_number, tee_id: c.tee_id || null, tees_in_play: c.tees_in_play || [] })) }),
                    });
                }

                // 4. Manage Groups (Bulk Update) - USE FINAL GROUPS
                const allRoundsGroupsPayload = finalTournamentData.groups.map(roundGroup => ({
                    round_number: roundGroup.round_number,
                    pairing_mode: roundGroup.pairingMode || 'manual',
                    groups: roundGroup.groups
                        .filter(g => !g.placeholder && (g.players || []).length > 0)
                        .map(group => ({
                            player_ids: (group.players || []).map(p => p.id)
                        }))
                }));

                await authenticatedFetch(`/tournaments/${tournamentIdToUse}/groups`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        all_rounds_groups: allRoundsGroupsPayload,
                        initiate_rounds: false // Do NOT auto-initiate
                    }),
                });

                // 5. Per-player tees, for rounds played off more than one tee.
                // Sent per round because that is how they are keyed, and only
                // for rounds the organiser actually CHANGED — a single-tee
                // event sends nothing at all, and an untouched round is never
                // re-sent mid-event: the server refuses a round holding a
                // FINALISED card, so re-posting Round 1's unchanged assignments
                // after it was played would 403 on every subsequent save.
                // Must follow the courses call: a tee can only be assigned once
                // its round declares it.
                const playerTeesByRound = finalTournamentData.player_tees || {};
                const originalTeesByRound = (isEditMode && originalTournamentData?.player_tees) || {};
                const sameAssignments = (a = {}, b = {}) =>
                    currentTournamentPlayerIds.every(pid => (a[pid] ?? null) === (b[pid] ?? null));
                const changedTeeRounds = Object.entries(playerTeesByRound).filter(
                    ([roundNumber, byPlayer]) =>
                        !isEditMode || !sameAssignments(byPlayer, originalTeesByRound[roundNumber])
                );
                // A silently swallowed refusal here is the worst outcome: the
                // wizard reports success and the field is still on the old tee.
                const teeErrors = (await Promise.all(changedTeeRounds.map(async ([roundNumber, byPlayer]) => {
                    // Every player on the roster is sent so that clearing an
                    // assignment back to the main tee is expressed explicitly.
                    const assignments = {};
                    currentTournamentPlayerIds.forEach(pid => {
                        assignments[pid] = byPlayer[pid] ?? null;
                    });
                    try {
                        const res = await authenticatedFetch(
                            `/tournaments/${tournamentIdToUse}/rounds/${roundNumber}/player-tees`,
                            {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ assignments }),
                            }
                        );
                        if (res.ok) return null;
                        const body = await res.json().catch(() => ({}));
                        return `Round ${roundNumber}: ${body.error || 'tees could not be saved.'}`;
                    } catch (e) {
                        return `Round ${roundNumber}: tees could not be saved.`;
                    }
                }))).filter(Boolean);
                if (teeErrors.length > 0) {
                    notify.error(`Everything else was saved, but the tee changes were not:\n\n${teeErrors.join('\n')}`);
                }
            }
            
            // Link this event to any selected manual-mode Orders of Merit
            // (best-effort; an already-linked OOM returns 409, which we ignore).
            if (oomLinks.length > 0) {
                await Promise.all(oomLinks.map(oomId =>
                    authenticatedFetch(`/oom/${oomId}/events`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ tournament_id: tournamentIdToUse, round_number: null, weight: 1 }),
                    }).catch(() => {})
                ));
            }

            // Invalidate any React Query caches that other pages (Rounds, Leaderboard,
            // Tournaments list) may have for this tournament. Without this, e.g. a freshly
            // assigned player still shows as "Unassigned" on the Rounds banner after the
            // user returns from the wizard, because round-data is served from stale cache.
            queryClient.invalidateQueries(['roundData', tournamentIdToUse]);
            queryClient.invalidateQueries(['courses', tournamentIdToUse]);
            queryClient.invalidateQueries(['tournaments']);
            queryClient.invalidateQueries(['matchplayLeaderboard', tournamentIdToUse]);
            queryClient.invalidateQueries(['liveLeaderboard', tournamentIdToUse]);

            // A QUICK ROUND IS SAVED, NOT STARTED, and goes to the Rounds page.
            // Starting it here (/initiate_round, straight to the card) was tried
            // in e1c0f8f and reverted on the owner's call: the Rounds page is
            // where the playing handicaps and the course/tee are checked before
            // anyone tees off, and skipping it fixed those figures unseen.
            if (isQuickMode && !isEditMode && signup_mode !== 'roll_up') {
                clearQuickDraft(draftSocietyId, draftKind);
                navigate('/tournaments/scorecard-entry', { state: { tournamentId: tournamentIdToUse } });
                return;
            }

            clearQuickDraft(draftSocietyId, draftKind);
            setCreatedTournamentId(tournamentIdToUse);
            setShowModal(true);
        } catch (error) {
            console.error(`Error ${isEditMode ? 'updating' : 'creating'} tournament:`, error);
            notify.error(`Error: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEventTypeSelect = (eventType) => {
        // Some event types hand off to a dedicated flow (e.g. the Advanced
        // Competitions wizard) rather than continuing in this wizard.
        if (eventType.route) {
            navigate(eventType.route);
            return;
        }
        // Switching to a different event type starts from a clean draft. The
        // quick round auto-names itself from the chosen course ("Course - dd/mm")
        // and that name, plus the course itself, used to survive the trip back
        // through the selector and turn up on a Multi Round or Matchplay setup.
        // Re-picking the SAME type keeps the draft, so a stray back-tap doesn't
        // throw away work.
        const isSwitch = eventTypeKey !== null && eventType.key !== eventTypeKey;
        setTournamentData(prev => {
            const next = {
                ...(isSwitch ? emptyTournamentData(initialFormat) : prev),
                ...eventType.defaults,
            };
            // A clean draft still has you in it — and a roll up never does,
            // even when it inherits the draft the selector opened on.
            if (next.signup_mode === 'roll_up') return { ...next, players: [] };
            return seedSelf(next, myPlayer || fetchedMe);
        });
        setEventTypeKey(eventType.key);
        if (isSwitch) setOomLinks([]); // part of the draft, not of the wizard's chrome
        if (isSwitch) setRestoredDraft(null); // the note described the draft just set aside
        if (eventType.quickMode) {
            // Navigate to quick mode URL so the wizard uses quick mode flow
            navigate('/tournaments/add?mode=quick', { replace: true });
        }
        setEventTypeSelected(true);
    };

    const showEventTypeSelector = !eventTypeSelected && !isEditMode && !isQuickMode;

    // CANCEL. Setup runs full screen — no tab bar to tap by accident — so this
    // is the one deliberate way out. A new setup is already kept on the phone,
    // so leaving loses nothing and asks nothing; it says so instead. An edit
    // has no draft, so leaving one somebody has changed asks first.
    const leaveSetup = () => {
        setConfirmLeave(false);
        if (isEditMode) {
            navigate(memberMode ? '/tournaments?mine=true' : '/tournaments');
        } else if (location.key !== 'default') {
            navigate(-1);
        } else {
            navigate(canCreateRound || hasPermission(Permission.CREATE_TOURNAMENT) ? '/setup' : '/play');
        }
    };
    const handleCancel = () => {
        if (isEditMode) {
            if (touched) setConfirmLeave(true);
            else leaveSetup();
            return;
        }
        if (draftSocietyId && hasWorthKeeping) {
            notify.success(isQuickMode ? 'Round saved on this phone' : 'Saved on this phone');
        }
        leaveSetup();
    };

    const getPrimaryButtonText = () => {
        if (isQuickMode) return 'Start Game';
        if (isRollUp) {
            if (!isEditMode) return 'Create Tee Sheet';
            if (rollUpConfigChanged) return 'Update Tee Sheet';
            return 'View Tee Sheet';
        }
        if (isEditMode) return 'Update';
        return 'Create Tournament';
    };

    const draftNote = restoredDraft && !isEditMode && (
        <div className="wiz-draft-note" role="status">
            <span>{isQuickMode ? 'Picked up your unfinished round.' : 'Picked up your unfinished competition.'}</span>
            <button type="button" className="wiz-draft-fresh" onClick={startFresh}>Start fresh</button>
        </div>
    );

    return (
        <React.Fragment>
            <header className="wizard__header">
                <button className="wizard__back-btn" onClick={() => {
                    if (isEditMode) {
                        navigate(memberMode ? '/tournaments?mine=true' : '/tournaments');
                    } else if (showEventTypeSelector) {
                        navigate('/tournaments');
                    } else if (currentStep > 1) {
                        prevStep();
                    } else if (urlQuickMode) {
                        // A quick round is launched deliberately from Manage or
                        // Play, so the event-type picker was never part of this
                        // flow — dropping into it on back reads as a wrong turn.
                        // Return to whichever screen launched it.
                        if (location.key !== 'default') navigate(-1);
                        else navigate('/setup');
                    } else if (memberMode) {
                        navigate('/setup');
                    } else if (!hasPermission(Permission.CREATE_TOURNAMENT)) {
                        navigate('/play');
                    } else {
                        // Non-quick admin flow only: step back to the picker.
                        setEventTypeSelected(false);
                    }
                }}>
                    <FiChevronLeft size={24} />
                </button>
                {!showEventTypeSelector && (
                    <button type="button" className="wizard__cancel-btn" onClick={handleCancel}
                            aria-label={isEditMode ? 'Cancel editing' : 'Cancel setup'}>
                        <FiX size={22} />
                    </button>
                )}
                <SocietyHeaderBadge />
                <h1>
                    {showEventTypeSelector ? 'New Competition' : (isEditMode ? (memberMode ? 'Edit Quick Round' : 'Edit Tournament') : (isQuickMode ? 'Quick Round Setup' : 'Add Tournament'))}
                </h1>
            </header>

            {!showEventTypeSelector && (
                <div className="wizard__progress-bar">
                    {progressSteps}
                </div>
            )}

            <main className={`wizard__content${showEventTypeSelector ? ' wizard__content--no-progress' : ' wizard__content--fullscreen'}${isQuickMode && currentStep === 1 ? ' wiz-quick-basics' : ''}`}
                  onPointerDownCapture={() => { if (!touched) setTouched(true); }}
                  onKeyDownCapture={() => { if (!touched) setTouched(true); }}>
                {showEventTypeSelector ? (
                    <EventTypeSelector onSelect={handleEventTypeSelect} />
                ) : isQuickMode ? (
                    <>
                        {draftNote}
                        {currentStep === 1 && <Step1_QuickSetup data={tournamentData} updateData={updateTournamentData} allCourses={allCourses} onCourseAdopted={(c) => setAllCourses((prev) => (prev.some((x) => x.id === c.id) ? prev : [...prev, c]))} membersMayAddCourses={isQuickMode} isLocked={isTournamentStarted} isEditMode={isEditMode} tournamentId={tournamentData.id} />}
                        {currentStep === 1 && <Step3_Scoring quick data={tournamentData} updateData={updateTournamentData} isLocked={isTournamentStarted} showSignupType={false} />}
                        {currentStep === 2 && <Step3_Players data={tournamentData} updateData={updateTournamentData} isLocked={isTournamentStarted} suggestedPlayers={suggestedPlayers} />}
                        {currentStep === 3 && <Step4_OrganizeGroups data={tournamentData} updateData={updateTournamentData} initialRound={location.state?.initialRound} onPickerOpenChange={setPlayerPickerOpen} selectedRound={groupsSelectedRound} onSelectedRoundChange={setGroupsSelectedRound} />}
                    </>
                ) : (
                    <>
                        {draftNote}
                        {currentStep === 1 && <Step1_Details data={tournamentData} updateData={updateTournamentData} isLocked={isTournamentStarted} isEditMode={isEditMode} tournamentId={tournamentData.id} authenticatedFetch={authenticatedFetch} />}
                        {currentStep === 2 && <Step2_Courses data={tournamentData} updateData={updateTournamentData} isLocked={isTournamentStarted} singleRound={!!tournamentData.is_advanced} />}
                        {currentStep === 3 && (
                            <div className="wiz-compact">
                                <Step3_Scoring data={tournamentData} updateData={updateTournamentData} isLocked={isTournamentStarted} showSignupType={true} />
                                {availableOoms.length > 0 && (
                                    <div className="wiz-step">
                                        <div className="wiz-field">
                                            <label className="wiz-label">Order of Merit</label>
                                            <p className="wiz-hint">Choose which season rankings this event counts toward.</p>
                                            <div className="wiz-pills wiz-pills--row">
                                                <button type="button"
                                                    className={`wiz-pill ${oomLinks.length === 0 ? 'wiz-pill--active' : ''}`}
                                                    onClick={() => setOomLinks([])}>
                                                    Not qualifying
                                                </button>
                                                {availableOoms.map(o => (
                                                    <button key={o.id} type="button"
                                                        className={`wiz-pill ${oomLinks.includes(o.id) ? 'wiz-pill--active' : ''}`}
                                                        onClick={() => setOomLinks(prev => prev.includes(o.id)
                                                            ? prev.filter(x => x !== o.id) : [...prev, o.id])}>
                                                        {o.name}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {currentStep === 4 && (
                            tournamentData.signup_mode === 'roll_up'
                            ? <Step3_RollUpConfig data={tournamentData} updateData={updateTournamentData} isLocked={isTournamentStarted} />
                            // The regulars belong on EVERY players step, not
                            // just the quick round's. A multi-round
                            // competition is the setup with the most typing in
                            // it, and it was the one flow that never offered
                            // the shortcut.
                            : <Step3_Players data={tournamentData} updateData={updateTournamentData} isLocked={isTournamentStarted} suggestedPlayers={suggestedPlayers} />
                        )}

                        {currentStep === 5 && tournamentData.signup_mode !== 'roll_up' && (
                            <Step4_OrganizeGroups data={tournamentData} updateData={updateTournamentData} initialRound={location.state?.initialRound} onPickerOpenChange={setPlayerPickerOpen} selectedRound={groupsSelectedRound} onSelectedRoundChange={setGroupsSelectedRound} />
                        )}
                    </>
                )}
            </main>

            {!showEventTypeSelector && !playerPickerOpen && (
                <footer className="wizard__navigation wizard__navigation--fullscreen">
                    {isOnGroupsStep && hasMultipleRounds && !isEditMode && !isOnLastGroupsRound ? (
                        <>
                            <button onClick={handleAdvanceRound} className="btn-primary">
                                Continue to Round {nextGroupsRoundNumber}
                            </button>
                            <button
                                onClick={handleFinish}
                                className="wizard__finish-now-link"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Saving…' : `Finish setup now — set later rounds later`}
                            </button>
                        </>
                    ) : (
                        <div className={`wizard__nav-row${showTeeShortcut ? ' wizard__nav-row--split' : ''}`}>
                            {showTeeShortcut && (
                                <button
                                    className="wizard__teesheet-btn"
                                    onClick={() => navigate(`/tournaments/${tournamentData.id}/teesheet`)}
                                >
                                    <FiGrid size={16} /> Tee sheet
                                </button>
                            )}
                            {currentStep < totalSteps ? (
                                <button onClick={nextStep} className="btn-primary">
                                    {isQuickMode ? (
                                        currentStep === 1 ? 'Add Players' : 'Assign Groups'
                                    ) : (
                                        currentStep === 1 ? 'Select Courses' :
                                        currentStep === 2 ? 'Set Scoring' :
                                        currentStep === 3 ? (tournamentData.signup_mode === 'roll_up' ? 'Configure Tee Sheet' : 'Add Players') :
                                        'Assign Groups'
                                    )}
                                </button>
                            ) : (
                                <button
                                    onClick={handleFinish}
                                    className="btn-primary"
                                    disabled={isSubmitting || (isRollUp && !rollUpComplete)}
                                >
                                    {isSubmitting ? 'Saving...' : getPrimaryButtonText()}
                                </button>
                            )}
                        </div>
                    )}

                </footer>
            )}

            <ConfirmationModal 
                tone="success"
                isOpen={showModal}
                title={isQuickMode ? "Ready to Play!" : (tournamentData.signup_mode === 'roll_up' ? "Tee Sheet Ready" : "Tournament Ready!")}
                message={isQuickMode ? "Your round is set up. Head to the tee!" : (tournamentData.signup_mode === 'roll_up' ? "Tee sheet generated successfully!" : "Tournament Ready")}
                confirmText={isQuickMode ? "Go to Scorecard" : (tournamentData.signup_mode === 'roll_up' ? "View Tee Sheet" : "Go to Tournament")}
                cancelText="Later"
                onConfirm={() => {
                    if (isQuickMode || tournamentData.signup_mode === 'standard') {
                        navigate('/tournaments/scorecard-entry', { state: { tournamentId: createdTournamentId } });
                    } else if (tournamentData.signup_mode === 'roll_up') {
                         navigate(`/tournaments/${createdTournamentId}/teesheet`);
                    }
                }}
                onCancel={() => navigate(memberMode ? '/tournaments?mine=true' : '/tournaments')}
            />

            <ConfirmationModal
                isOpen={confirmLeave}
                title="Discard your changes?"
                message={`Your changes to ${tournamentData.name || 'this competition'} haven't been saved.`}
                confirmText="Discard"
                cancelText="Keep editing"
                onConfirm={leaveSetup}
                onCancel={() => setConfirmLeave(false)}
            />

            {/* Only the event-type picker keeps the tab bar: it is a menu, with
                nothing typed yet to lose. Once an event is being set up the
                screen is the task's, and Cancel is the way out. */}
            {showEventTypeSelector && (
                <BottomNav menuItems={[
                    { name: 'Leaderboard', route: '/leaderboard', icon: <FiBarChart2 size={24} /> },
                    { name: 'Rounds', route: '/tournaments/scorecard-entry', icon: <FiGrid size={24} /> },
                    { name: 'Competitions', route: '/tournaments', icon: <FiAward size={24} /> },
                    { name: 'Manage', route: '/setup', icon: <FiSettings size={24} /> },
                ]} />
            )}
        </React.Fragment>
    );
};

export default AddTournamentWizard;